import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Menu, Phone, X } from 'lucide-react';
import Logo from './Logo';
import { JANE_URL, NAV_LINKS, PHONE, PHONE_HREF } from '../data/site';

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
          scrolled
            ? 'bg-brand-cream/80 shadow-[0_1px_0_0_rgba(19,42,38,0.08)] backdrop-blur-xl'
            : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-10">
          <Link to="/" data-testid="nav-home-link" aria-label="Thula Doula Wellness — home">
            <Logo dark={open} />
          </Link>

          <nav className="hidden items-center gap-7 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `link-underline text-[13px] font-medium tracking-wide text-brand-ink/80 transition-colors duration-300 hover:text-brand-teal ${
                    isActive ? 'text-brand-teal' : ''
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="nav-book-btn"
              href={JANE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group hidden items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-[13px] font-semibold tracking-wide text-brand-cream shadow-[0_10px_30px_-12px_rgba(32,86,82,0.6)] transition-[background-color,transform,box-shadow] duration-300 hover:bg-brand-ink hover:shadow-[0_14px_34px_-10px_rgba(19,42,38,0.55)] active:scale-[0.97] sm:inline-flex"
            >
              Book a Massage
              <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              className={`inline-flex size-11 items-center justify-center rounded-full border transition-[background-color,color] duration-300 ${
                open
                  ? 'border-brand-cream/30 text-brand-cream hover:bg-brand-cream hover:text-brand-ink'
                  : 'border-brand-ink/15 text-brand-ink hover:bg-brand-ink hover:text-brand-cream'
              } xl:hidden`}
            >
              {open ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            data-lenis-prevent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col bg-brand-ink px-6 pb-10 pt-28 xl:hidden"
          >
            <nav className="flex flex-1 flex-col justify-center gap-1" aria-label="Mobile">
              {[{ label: 'Home', path: '/', testId: 'nav-link-home' }, ...NAV_LINKS].map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={l.path}
                    data-testid={l.testId}
                    className="group flex items-baseline gap-4 border-b border-brand-cream/10 py-4"
                  >
                    <span className="font-display text-3xl text-brand-cream transition-colors duration-300 group-hover:text-brand-sage">
                      {l.label}
                    </span>
                    <ArrowUpRight className="size-5 self-center text-brand-sage/60 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="mt-8 flex flex-col gap-4"
            >
              <a
                data-testid="mobile-book-btn"
                href={JANE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-cream px-8 py-4 text-base font-semibold text-brand-ink transition-transform duration-300 active:scale-[0.98]"
              >
                Book a Massage <ArrowUpRight className="size-5" />
              </a>
              <a
                href={PHONE_HREF}
                data-testid="mobile-phone-link"
                className="flex items-center justify-center gap-2 text-sm text-brand-cream/70"
              >
                <Phone className="size-4" /> {PHONE}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
